import { Button, Dialog, DialogContent, DialogDescription, DialogTitle } from "@platform/ui";
import { codeValue, ErrorText, errorKey, Field, useSession, useText } from "@platform/ui/features";
import { type FormEvent, type ReactNode, useRef, useState } from "react";

type Outcome = { error?: unknown };
type Action = () => Promise<Outcome>;

/**
 * Runs an action that may need a fresh TOTP step-up (routes marked `step-up`): on STEP_UP_REQUIRED it asks for a
 * code, elevates the session, and runs the action once more.
 */
export function useStepUp(): {
  run: (action: () => Promise<Outcome>) => Promise<Outcome>;
  dialog: ReactNode;
} {
  const { t } = useText();
  const { session } = useSession();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | undefined>();
  const pending = useRef<((ok: boolean) => void) | undefined>(undefined);

  const confirm = async (event: FormEvent) => {
    event.preventDefault();
    const { data, error: problem } = await session.api.POST("/v1/ops/auth/step-up", { body: { code } });
    if (data === undefined) {
      setCode("");
      return setError(t(errorKey(problem)));
    }
    session.takeAccessToken(data.accessToken);
    setOpen(false);
    pending.current?.(true);
  };

  const run = async (action: Action): Promise<Outcome> => {
    const first = await action();
    if ((first.error as { code?: string } | undefined)?.code !== "STEP_UP_REQUIRED") return first;
    setCode("");
    setError(undefined);
    setOpen(true);
    const confirmed = await new Promise<boolean>((resolve) => {
      pending.current = resolve;
    });
    return confirmed ? action() : first;
  };

  const dialog = (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) pending.current?.(false);
      }}
    >
      <DialogContent>
        <DialogTitle>{t("admin.stepUp.title")}</DialogTitle>
        <DialogDescription>{t("admin.stepUp.prompt")}</DialogDescription>
        <form className="mt-4 flex flex-col gap-3" onSubmit={(event) => void confirm(event)}>
          <Field
            id="step-up-code"
            label={t("auth.codeLabel")}
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(event) => setCode(codeValue(event.target.value))}
          />
          {error !== undefined && <ErrorText>{error}</ErrorText>}
          <Button type="submit" disabled={code.length !== 6}>
            {t("admin.stepUp.confirm")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
  return { run, dialog };
}
